/* eslint-disable complexity */
import { useOutsideClick } from '@toggle/helpers';
import React, {
  forwardRef,
  ForwardRefExoticComponent,
  MouseEvent,
  ReactNode,
  RefAttributes,
  RefObject,
  TextareaHTMLAttributes,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router';

import { useRows } from '~/components/chat';
import { ChatModesButtonGroup } from '~/components/chat-landing/ChatModesButtonGroup';
import { RenderFeatureFlag } from '~/components/render-feature-flag/RenderFeatureFlag';
import { appPaths } from '~/routes/app-paths';
import { Statistics } from '~/views/alfred/document-search/document-sources-view/document-sources-table/use-document-sources-table-store/useDocumentSourcesTableStore';
import { useDocumentSearch } from '~/views/alfred/document-search/use-document-search/useDocumentSearch';
import { useUserUpload } from '~/views/alfred/research/useUserUpload/useUserUpload';
import {
  ChatMode,
  useChatNavigation,
} from '~/views/alfred/use-chat-navigation/useChatNavigation';

import { ChatInputContextHeader } from '../chat-input-context-header/ChatInputContextHeader';
import { UploadDropdown } from '../upload-dropdown/UploadDropdown';
import { UploadFileStatusDropdown } from '../upload-file-status-dropdown/UploadFileStatusDropdown';
import { AnalysisModeDropdown } from './AnalysisModeDropdown';
import * as S from './ChatDocumentContextInput.styles';
import { WebSearchDropdown } from './web-search/WebSearchDropdown';

export interface ChatDocumentContextInputProps
  extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  children?: ReactNode;
  inputRef?: RefObject<HTMLTextAreaElement | null>;
  isMessagePending: boolean;
  contextCounts?: {
    documentsCount: number;
    subTypeCount: number;
    statistics?: Statistics;
  };
  allowedModes: ChatMode[];
}

export const ChatDocumentContextInput = forwardRef<
  HTMLDivElement,
  ChatDocumentContextInputProps
>(
  (
    {
      children,
      onChange,
      value,
      isMessagePending,
      inputRef,
      contextCounts,
      allowedModes,
      ...rest
    },
    ref
  ) => {
    const wrapperRef = useRef<HTMLDivElement>(null);
    const { rows, updateRows } = useRows({
      value: value,
      textAreaRef: inputRef,
    });

    const { mode, previousMode } = useChatNavigation();
    const location = useLocation();
    const isAlfredPage = location.pathname === appPaths.alfred;
    const hasActiveDocumentMessage = useDocumentSearch(
      state => !state.isMessagePending && !!state.messages.length
    );
    const { files } = useUserUpload(state => ({
      files: state.files,
    }));

    const [showTools, setShowTools] = useState(isAlfredPage ? true : false);

    const handleIsClickedOutside = (
      ref: RefObject<HTMLDivElement | null>,
      e: MouseEvent<Element, globalThis.MouseEvent>
    ) => {
      if (isAlfredPage) {
        return false;
      }

      const target = e.target;

      if (!(target instanceof HTMLElement)) {
        return false;
      }

      const withinTarget = ref.current?.contains(target);
      const isInsideChatDropdown = target.closest(
        '#upload-dropdown-content, #analysis-mode-dropdown-content'
      );
      const isInsideDropdownList = target.closest('#dropdown-list');
      const isInsideTooltip = target.closest('#tooltip-content');

      if (
        withinTarget ||
        isInsideChatDropdown ||
        isInsideDropdownList ||
        isInsideTooltip
      ) {
        setShowTools(true);
        return false;
      }

      setShowTools(false);
      return true;
    };

    useOutsideClick({
      ref: wrapperRef,
      callback: () => {},
      isClickedOutside: handleIsClickedOutside,
      eventType: 'mousedown',
    });

    useEffect(() => {
      if (isAlfredPage) {
        setShowTools(true);
      } else {
        setShowTools(previousMode !== null);
      }
    }, [isAlfredPage, previousMode]);

    const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      updateRows(event);
      onChange?.(event);
    };

    const onKeyDown: React.KeyboardEventHandler<HTMLTextAreaElement> = e => {
      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
        e.stopPropagation();
      } else {
        rest.onKeyDown?.(e);
      }
    };

    const hasValidFiles = files.filter(file => file.status !== 'error').length;

    const showToolsOnNonAlfredPage = !isAlfredPage && showTools;
    const showToolsOnAlfredPage = isAlfredPage && mode === 'research';

    const showChildrenInTextArea =
      (isAlfredPage && mode !== 'research') || (!isAlfredPage && !showTools);
    const showChildrenInTools =
      !isAlfredPage || (isAlfredPage && mode === 'research');

    return (
      <S.ChatInputFieldWrapper ref={ref} $isMessagePending={isMessagePending}>
        <S.ChatInputFieldContainer ref={wrapperRef}>
          {mode === 'document' && isAlfredPage && hasActiveDocumentMessage && (
            <ChatInputContextHeader contextCounts={contextCounts} />
          )}
          <RenderFeatureFlag requiredFlag={'alfred_user_upload'}>
            {mode === 'research' && !!files.length && (
              <S.UploadingSection $noPadding={!hasValidFiles}>
                <UploadFileStatusDropdown />
              </S.UploadingSection>
            )}
          </RenderFeatureFlag>
          <S.TextAreaWrapper>
            <S.TextareaElement
              id="dropdown-trigger"
              as="textarea"
              ref={inputRef}
              onChange={handleChange}
              rows={rows}
              value={value}
              {...rest}
              onFocus={() => {
                setShowTools(true);
              }}
              data-testid="chat-input"
              {...(mode === 'research' ? { onKeyDown } : {})}
            />
            {showChildrenInTextArea && children}
          </S.TextAreaWrapper>
          {(showToolsOnNonAlfredPage || showToolsOnAlfredPage) && (
            <S.AlfredTools>
              <S.ToolsWrapper>
                {isAlfredPage && (
                  <RenderFeatureFlag requiredFlag={'alfred_user_upload'}>
                    <UploadDropdown />
                  </RenderFeatureFlag>
                )}
                <S.LeftTools>
                  {!isAlfredPage && allowedModes.length > 1 && (
                    <ChatModesButtonGroup compactMode variant="secondary" />
                  )}
                  {mode === 'research' && (
                    <S.ResearchDropdowns>
                      <AnalysisModeDropdown
                        isMessagePending={isMessagePending}
                      />
                      <RenderFeatureFlag
                        requiredFlag={'alfred_trusted_sources'}
                      >
                        <S.ResearchDropdownsDivider>
                          <WebSearchDropdown />
                        </S.ResearchDropdownsDivider>
                      </RenderFeatureFlag>
                    </S.ResearchDropdowns>
                  )}
                </S.LeftTools>
              </S.ToolsWrapper>
              {showChildrenInTools && children}
            </S.AlfredTools>
          )}
        </S.ChatInputFieldContainer>
      </S.ChatInputFieldWrapper>
    );
  }
) as ForwardRefExoticComponent<
  ChatDocumentContextInputProps & RefAttributes<HTMLTextAreaElement>
> & {
  Styled: typeof S;
};

ChatDocumentContextInput.Styled = S;
